import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useCategoriesUnified } from '@/hooks/useCategoriesUnified';
import { Skeleton } from '@/components/ui/skeleton';
import { HierarchicalCategorySelector } from '@/components/common/HierarchicalCategorySelector';
import { AlertTriangle } from 'lucide-react';

interface CategorySelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
  categoryType: 'inventory' | 'store' | 'service';
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  hierarchical?: boolean;
}

export function CategorySelector({
  value,
  onValueChange,
  categoryType,
  placeholder = "Выберите категорию",
  label,
  required = false,
  disabled = false,
  className,
  hierarchical = false,
}: CategorySelectorProps): JSX.Element {
  const { categories, loading } = useCategoriesUnified({ type: categoryType });

  // Фильтруем категории по типу
  const filteredCategories = React.useMemo(() => {
    // категории уже отфильтрованы по типу хуком; оставляем только корневые
    return categories.filter(category => !category.parentId);
  }, [categories]);

  // Функция для получения полного пути категории (включая подкатегории)
  const getAllCategories = React.useMemo(() => {
    const result: Array<{ id: string; name: string; path: string }> = [];
    
    const addCategoriesRecursively = (cats: typeof categories, parentPath = '') => {
      cats.forEach(cat => {
        const currentPath = parentPath ? `${parentPath} → ${cat.name}` : cat.name;
        result.push({
          id: cat.id!,
          name: cat.name,
          path: currentPath
        });
        
        // Добавляем подкатегории на основе parentId
        const subcategories = categories.filter(subcat => subcat.parentId === cat.id);
        if (subcategories.length > 0) {
          addCategoriesRecursively(subcategories, currentPath);
        }
      });
    };
    
    addCategoriesRecursively(filteredCategories);
    return result;
  }, [categories, filteredCategories]);

  if (hierarchical) {
    return (
      <HierarchicalCategorySelector
        value={value ?? null}
        onValueChange={(v) => {
          // For backward compatibility, ignore null selections here
          if (v) onValueChange(v);
        }}
        categoryType={categoryType}
        label={label}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        showRootOption={false}
      />
    );
  }

  if (loading) {
    return (
      <div className={className}>
        {label && <Label className="mb-2 block">{label}</Label>}
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // Remove error handling for now since useCategories doesn't return error

  if (getAllCategories.length === 0) {
    return (
      <div className={className}>
        {label && <Label className="mb-2 block">{label}</Label>}
        <div className="flex items-center gap-2 p-3 border rounded-md bg-muted/50 text-muted-foreground">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm">
            Категории типа "{categoryType === 'inventory' ? 'Инвентарь' : categoryType === 'store' ? 'Магазин' : 'Услуги'}" не найдены
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <Label className="mb-2 block">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <Select 
        value={value} 
        onValueChange={onValueChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} className="truncate" />
        </SelectTrigger>
        <SelectContent>
          {getAllCategories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              <span className="truncate block">{category.path}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
